import { MODES, type Mode } from "../content/modes";
import { rankFor } from "../game/ranks";
import { updateSets } from "../game/sets";
import { addStripes, stripesFor } from "../game/stripes";
import { trialFor } from "../game/trials";
import type { SaveState } from "./schema";
import type { SaveV1 } from "./schemaV1";

/**
 * Phase 1 → version 2. Nobody loses a rank or an unlock date:
 * - rank = the rank their best score reached under the old thresholds;
 * - old unlock dates become "passed" dates on those ranks' trials;
 * - the old best becomes the best on the trial that earned the current rank
 *   (or on the first trial, for a child still at Seedling);
 * - mission length and ranges move to the Practice settings;
 * - old missions become trial history entries without a rank.
 *
 * Returns plain data for the v2 schema to validate.
 */
export function migrateV1(v1: SaveV1) {
  const rank = {} as Record<Mode, number>;
  const trials: Partial<
    Record<Mode, Record<string, { best: number; attempts: number; passed?: string }>>
  > = {};

  for (const mode of MODES) {
    const best = v1.best[mode] ?? 0;
    const r = rankFor(best);
    rank[mode] = r;
    const records: Record<string, { best: number; attempts: number; passed?: string }> = {};
    for (const [i, date] of Object.entries(v1.unlocks[mode] ?? {}))
      if (Number(i) >= 1) records[i] = { best: 0, attempts: 0, passed: date };
    if (best > 0) {
      const at = String(Math.max(1, r));
      records[at] = { ...(records[at] ?? { attempts: 0 }), best };
    }
    if (Object.keys(records).length) trials[mode] = records;
  }

  const hasProgress = v1.history.length > 0 || MODES.some((m) => (v1.best[m] ?? 0) > 0);

  return {
    version: 2,
    name: v1.name,
    mode: v1.mode,
    settings: {
      ops: v1.settings.ops,
      audio: false,
      practice: {
        minutes: v1.settings.minutes,
        addMax: v1.settings.addMax,
        tableMax: v1.settings.tableMax,
      },
    },
    rank,
    trials,
    history: v1.history.map((h) => ({
      d: h.d,
      mode: h.mode,
      kind: "trial",
      score: h.score,
      tried: h.tried,
      seconds: Math.round(h.mins * 60),
      audio: false,
    })),
    mastery: {},
    missed: {},
    onboarded: hasProgress,
  };
}

/**
 * Version 2 → 3 (v1.1: stripes, sets and the smoother ladder). Takes a save
 * already parsed into the current shape, with the new fields at defaults:
 * - stripes come from each trial's best, using the new stripe marks;
 * - gold and foil cards and the overall rank come from the ranks held.
 * The one-off rank award for bests that meet a new, lower pass mark is not
 * made here: it's a rank event (see game/retune.ts), so every device agrees.
 * Pure.
 */
export function migrateV2(save: SaveState, today: string): SaveState {
  let stripes = save.stripes;
  for (const mode of MODES)
    for (const [key, r] of Object.entries(save.trials[mode] ?? {})) {
      const spec = trialFor(mode, Number(key));
      if (spec)
        stripes = addStripes(stripes, mode, spec.rankIndex, stripesFor(r.best, spec.stripes));
    }
  const sets = updateSets(
    { rank: save.rank, ops: save.settings.ops, sets: save.sets, overall: save.overall },
    today,
  );
  return { ...save, stripes, sets: sets.sets, overall: sets.overall };
}
